import { useEffect, useRef } from "react";
import { AlertTriangle } from "lucide-react";
import { useBlocker } from "react-router-dom";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export const useUnsavedGuard = (dirty) => {
  const leaving = useRef(false);
  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty && currentLocation.pathname !== nextLocation.pathname);
  const stay = () => { if (!leaving.current && blocker.state === "blocked") blocker.reset(); };
  const go = () => { leaving.current = true; blocker.proceed(); setTimeout(() => { leaving.current = false; }, 0); };

  useEffect(() => {
    if (!dirty) return undefined;
    const onUnload = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
  }, [dirty]);

  return (
    <AlertDialog open={blocker.state === "blocked"} onOpenChange={(o) => !o && stay()}>
      <AlertDialogContent data-testid="unsaved-dialog" overlayClassName="z-[100] backdrop-blur-sm" className="z-[101] w-[calc(100%-2rem)] max-w-lg rounded-lg border-t-4 border-t-[#E60000] bg-white text-[#111] p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
        <AlertDialogHeader className="text-left">
          <div className="w-12 h-12 mb-2 rounded-full bg-red-50 text-[#B30000] grid place-items-center" aria-hidden="true"><AlertTriangle className="w-7 h-7" /></div>
          <AlertDialogTitle className="font-heading font-extrabold text-xl" data-testid="unsaved-title">Leave without saving?</AlertDialogTitle>
          <AlertDialogDescription className="text-gray-800 leading-relaxed" data-testid="unsaved-description">Your event changes have not been saved. <strong className="block mt-2 text-[#B30000]">Leaving now will discard these changes.</strong></AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2 sm:space-x-0 mt-2">
          <AlertDialogCancel onClick={stay} className="mt-0 bg-[#111] text-white hover:bg-[#262626] hover:text-white h-auto min-h-11 whitespace-normal" data-testid="unsaved-stay">Stay and keep editing</AlertDialogCancel>
          <AlertDialogAction onClick={go} className="border border-red-300 bg-white text-[#B30000] hover:bg-red-50 h-auto min-h-11 whitespace-normal" data-testid="unsaved-leave">Leave without saving</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
